import { Button, ButtonProps, IconButton, makeStyles } from "@material-ui/core"
import { CSSProperties, MouseEventHandler, ReactNode } from "react"
import clsx from "clsx"

type HourReportActionButtonProps = {
  icon: string
  color?: "default" | "primary" | "delete"
  variant?: ButtonProps["variant"]
  children?: ReactNode
  disabled?: boolean
  onClick?: MouseEventHandler<HTMLButtonElement>
  style?: CSSProperties
  title?: string
  "aria-label"?: string
}

export default function HourReportActionButton({
  icon,
  color = "default",
  variant,
  children,
  ...buttonProps
}: HourReportActionButtonProps) {
  const classes = useStyles()
  const iconElement = <span className={clsx("material-symbols-rounded", classes.icon)}>{icon}</span>

  if (!children) {
    return (
      <IconButton
        disableRipple
        className={clsx(
          classes.iconButton,
          color === "primary" && classes.primaryIconButton,
          color === "delete" && classes.deleteIconButton
        )}
        {...buttonProps}
      >
        {iconElement}
      </IconButton>
    )
  }

  return (
    <Button
      type="button"
      variant={variant ?? (color === "primary" ? "contained" : "outlined")}
      color={color === "primary" ? "primary" : "default"}
      className={clsx(color === "delete" && classes.deleteButton)}
      startIcon={iconElement}
      {...buttonProps}
    >
      {children}
    </Button>
  )
}

const useStyles = makeStyles((theme) => ({
  iconButton: {
    boxSizing: "border-box",
    width: 32,
    minWidth: 32,
    height: 32,
    minHeight: 32,
    padding: 0,
    borderRadius: 6,
    backgroundColor: "rgba(20, 20, 20, 0.04)",
    color: theme.palette.common.black,
    transition: "background-color 120ms ease, color 120ms ease, opacity 120ms ease, transform 120ms ease",
    transform: "none",

    "&:hover, &:focus-visible, &.Mui-focusVisible": {
      backgroundColor: "rgba(20, 20, 20, 0.1)",
      transform: "scale(1.02)",
    },
  },
  primaryIconButton: {
    color: theme.palette.primary.main,
    backgroundColor: "rgba(22, 117, 179, 0.06)",

    "&:hover, &:focus-visible, &.Mui-focusVisible": {
      color: theme.palette.common.white,
      backgroundColor: theme.palette.primary.main,
    },
  },
  deleteIconButton: {
    color: theme.palette.error.main,
    backgroundColor: "rgba(255, 51, 51, 0.06)",

    "&:hover, &:focus-visible, &.Mui-focusVisible": {
      color: theme.palette.common.white,
      backgroundColor: theme.palette.error.main,
    },
  },
  deleteButton: {
    color: theme.palette.error.main,

    "&.MuiButton-outlined": {
      borderColor: theme.palette.error.main,
    },
  },
  icon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 20,
    height: 20,
    fontSize: 20,
    lineHeight: 1,
  },
}))
